"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  IndianRupee,
  CalendarDays,
  Calendar,
  Hash,
} from "lucide-react";

import "../../../../style/InvoicePrintout.css";

type Role = "Designer" | "Milling Hub" | "Delivery";

interface InvoiceCase {
  workPaymentID: number;
  caseId: number;
  orderId?: number | null;
  description: string;
  caseDate: string | null;
  amount: number;
}

interface Invoice {
  invoiceID: number;
  id: string;

  userID: number;
  person: string;
  email: string;

  roleID: number;
  role: Role;

  startDate: string;
  endDate: string;

  period: string;

  payoutDate: string;
  payoutDateRaw: string;

  cases: number;
  paid: number;

  status: string;
  emailSent: boolean;

  caseDetails?: InvoiceCase[];
}

const currency = (n: number) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

export default function InvoicePrintout() {
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceLoaded, setInvoiceLoaded] = useState(false);

  useEffect(() => {
    const storedInvoice = sessionStorage.getItem("invoicePrintout");

    if (storedInvoice) {
      try {
        const parsedInvoice = JSON.parse(storedInvoice) as Invoice;

        setInvoice(parsedInvoice);
      } catch (error) {
        console.error("Failed to parse invoice data:", error);
      }
    }

    setInvoiceLoaded(true);
  }, []);
     
  useEffect(() => {
  if (!invoiceLoaded || !invoice) {
    return;
  }

  const timer = setTimeout(() => {
    handleDownloadPDF();
  }, 500);

  return () => clearTimeout(timer);
}, [invoiceLoaded, invoice]);

  if (!invoiceLoaded) {
    return null;
  }

  if (!invoice) {
    return (
      <div className="invoice-printout-empty">
        <div className="invoice-empty-icon">
          <FileText size={32} />
        </div>

        <h2>Invoice not found</h2>

        <p>No invoice information was provided.</p>

        <button
          type="button"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          Go Back
        </button>
      </div>
    );
  }

  const caseDetails = invoice.caseDetails ?? [];

  const handleDownloadPDF = async () => {
  try {
    const element = document.getElementById(
      "invoice-pdf-area"
    );

    if (!element) {
      console.error(
        "Invoice PDF area not found."
      );
      return;
    }

    const options = {
      margin: 8,

      filename: `${invoice.id}.pdf`,

      image: {
        type: "jpeg" as const,
        quality: 0.98,
      },

      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      },

      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait" as const,
      },

      pagebreak: {
        mode: [
          "avoid-all",
          "css",
          "legacy",
        ],
      },
    };

    await html2pdf()
      .set(options)
      .from(element)
      .save();

  } catch (error) {
    console.error(
      "Failed to download invoice PDF:",
      error
    );
  }
};
const formatCaseDate = (date: string | null) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="invoice-printout-page">

      {/* =====================================================
          TOP TOOLBAR
      ===================================================== */}

      <div className="invoice-printout-toolbar">

        <button
          type="button"
          className="invoice-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* <div className="invoice-toolbar-title">
          <FileText size={18} />
          <span>Invoice Preview</span>
        </div>

        <button
          type="button"
          className="invoice-print-btn"
          onClick={handlePrint}
        >
          <Download size={16} />
          Download Invoice PDF
        </button> */}

      </div>

      {/* =====================================================
          INVOICE PAPER
      ===================================================== */}

     <div
  className="invoice-paper"
  id="invoice-pdf-area"
>

        {/* =================================================
            INVOICE HEADER
        ================================================= */}

        <div className="invoice-paper-header">

          <div>
            <span className="invoice-label">
              PAYMENT INVOICE
            </span>

            <h1>
              {invoice.id}
            </h1>
          </div>

          <div className="invoice-paid">
            <CheckCircle2 size={16} />
            Paid
          </div>

        </div>

        {/* =================================================
            MEMBER INFORMATION
        ================================================= */}

        <div className="invoice-info-section">

          <div className="invoice-member">

            <div className="invoice-avatar">
              {invoice.person?.slice(0, 1).toUpperCase() || "U"}
            </div>

            <div>

              <span className="invoice-detail-label">
                Team Member
              </span>

              <h2>
                {invoice.person}
              </h2>

              <span className="invoice-role">
                {invoice.role}
              </span>

            </div>

          </div>

          <div className="invoice-meta">

            <div className="invoice-meta-item">

              <span>
                <CalendarDays size={14} />
                Payout Date
              </span>

              <strong>
                {invoice.payoutDate}
              </strong>

            </div>

            <div className="invoice-meta-item">

              <span>
                <Calendar size={14} />
                Billing Period
              </span>

              <strong>
                {invoice.period}
              </strong>

            </div>

          </div>

        </div>

        {/* =================================================
            SUMMARY
        ================================================= */}

        <div className="invoice-summary">

          <div className="invoice-summary-card">

            <div className="invoice-summary-icon">
              <Hash size={17} />
            </div>

            <div>

              <span>
                Total Cases
              </span>

              <strong>
                {invoice.cases}
              </strong>

            </div>

          </div>

          <div className="invoice-summary-card">

            <div className="invoice-summary-icon">
              <IndianRupee size={17} />
            </div>

            <div>

              <span>
                Total Paid
              </span>

              <strong>
                {currency(invoice.paid)}
              </strong>

            </div>

          </div>

        </div>

        {/* =================================================
            CASE DETAILS
        ================================================= */}

        <div className="invoice-case-section">

          <div className="invoice-section-heading">

            <div>

              <h3>
                Case Details
              </h3>

              <p>
                Case-wise payment breakdown
              </p>

            </div>

            <span>
              {invoice.cases} cases
            </span>

          </div>

          <div className="invoice-case-table-wrapper">

            <table className="invoice-case-table">

              <thead>

                <tr>

                  <th>
                    Case ID
                  </th>

                  <th>
                    Description
                  </th>

                  <th>
                    Case Date
                  </th>

                  <th className="text-right">
                    Amount
                  </th>

                </tr>

              </thead>

              <tbody>

                {caseDetails.length > 0 ? (

                  caseDetails.map((item, index) => (

                    <tr
                      key={
                        item.workPaymentID ||
                        item.caseId ||
                        index
                      }
                    >

                      <td>

                        <span className="case-id">
                          {item.caseId}
                        </span>

                      </td>

                      <td>
                        {item.description}
                      </td>

                      <td>
                         {formatCaseDate(item.caseDate)}
                      </td>

                      <td className="text-right">

                        <strong>
                          {currency(item.amount)}
                        </strong>

                      </td>

                    </tr>

                  ))

                ) : (

                  <tr>

                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "24px",
                      }}
                    >
                      No case details available
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </div>

        {/* =================================================
            TOTAL
        ================================================= */}

        <div className="invoice-total">

          <div>

            <span>
              Total payable
            </span>

            <small>
              {invoice.cases} completed cases
            </small>

          </div>

          <strong>
            {currency(invoice.paid)}
          </strong>

        </div>

        {/* =================================================
            NOTE
        ================================================= */}

        <div className="invoice-note">

          <CheckCircle2 size={16} />

          <span>
            This invoice has been settled in full.
          </span>

        </div>

        {/* =================================================
            FOOTER
        ================================================= */}

        <div className="invoice-paper-footer">

          <span>
            Invoice No: {invoice.id}
          </span>

          <span>
            Generated on{" "}
            {new Date().toLocaleDateString("en-IN")}
          </span>

        </div>

      </div>

    </div>
  );
}